import React, { useState, useEffect } from 'react';
import { DB } from '../data/database';
import { ROLES, WORLD_PORTS, CUT_TYPES } from '../data/constants';
import { formatPrice, calculateContainerTotalJPY, defaultFixedCosts } from '../utils/currency';
import { useLanguage } from '../translations/LanguageContext';
import { ContainerIcon, PlusIcon, ChevronDownIcon, CarIcon, EngineIcon, PartsIcon, CheckIcon, SearchIcon, EditIcon, DeleteIcon } from './Icons';
import { logActivity } from '../data/notifications';
import centralUpdateStatus from '../utils/updateUnitStatus';
import ContainerLoading from './ContainerLoading';
import ContainerSeal from './ContainerSeal';
import ContainerBL from './ContainerBL';
import ContainerArrival from './ContainerArrival';
import ContainerCostsEditor from './ContainerCostsEditor';
import CustomerFilter, { CustomerFilterBanner } from './CustomerFilter';
import TimelineProgress from './ui/TimelineProgress';
import { generatePackingListPDF } from '../utils/generateInvoicePDF';

const CONTAINER_STAGES = [
  { id: 'planning', label: 'Planning', icon: '📝' },
  { id: 'booked', label: 'Booked', icon: '📋' },
  { id: 'loading', label: 'Loading', icon: '📦' },
  { id: 'sealed', label: 'Sealed', icon: '🔒' },
  { id: 'in_transit', label: 'In Transit', icon: '🚢' },
  { id: 'at_port', label: 'At Port', icon: '⚓' },
  { id: 'delivered', label: 'Delivered', icon: '🏁' },
];

function ContainerTimeline({ status, timeline }) {
  const order = ['planning', 'booked', 'loading', 'sealed', 'shipped', 'in_transit', 'at_port', 'customs', 'delivered', 'closed'];
  const idx = order.indexOf(status);
  const completed = CONTAINER_STAGES.map(s => s.id).filter(id => {
    const sIdx = order.indexOf(id);
    return sIdx >= 0 && sIdx <= idx;
  });
  const current = completed[completed.length - 1] || 'planning';
  const tlData = {};
  (Array.isArray(timeline) ? timeline : []).forEach(e => {
    const stage = e.event_type || e.stage || e.status;
    if (stage && !tlData[stage]) tlData[stage] = { date: e.timestamp || e.date, note: e.note || e.notes };
  });
  return <TimelineProgress stages={CONTAINER_STAGES} currentStage={current} completedStages={completed} stageData={tlData} compact />;
}

const CONTAINER_SIZES = ['20ft', '40ft', '40ft_hc'];
const SIZE_LABELS = { '20ft': '20 ft', '40ft': '40 ft', '40ft_hc': '40 ft HC' };

const EXPANDED_STATUSES = [
  { id: 'planning',   name: 'Planning' },
  { id: 'booked',     name: 'Booked' },
  { id: 'loading',    name: 'Loading' },
  { id: 'sealed',     name: 'Sealed' },
  { id: 'shipped',    name: 'Shipped' },
  { id: 'in_transit', name: 'In Transit' },
  { id: 'at_port',    name: 'At Port' },
  { id: 'customs',    name: 'Customs' },
  { id: 'delivered',  name: 'Delivered' },
  { id: 'closed',     name: 'Closed' },
];

// ── Helper: get all units not already in a container ──────────
function getAvailableUnits(excludeContainerId) {
  const allUnits = [
    ...(DB.get('vehicles')   || []).map(u => ({ ...u, _type: 'vehicle' })),
    ...(DB.get('engines')    || []).map(u => ({ ...u, _type: 'engine'  })),
    ...(DB.get('spareParts') || []).map(u => ({ ...u, _type: 'part'    })),
  ];
  const containers = DB.get('containers') || [];
  const usedIds = new Set(
    containers
      .filter(c => !excludeContainerId || c.id !== excludeContainerId)
      .flatMap(c => c.units || [])
  );
  return allUnits.filter(u => !usedIds.has(u.id));
}

function getUnitName(u) {
  if (u._type === 'part' || (!u._type && u.partName)) {
    // Parts: show part name/category first, source vehicle in parentheses
    const partLabel = u.partName || u.category || 'Part';
    const source = u.make ? ` — ${u.make} ${u.model || ''}`.trim() : '';
    return `${partLabel}${source}`;
  }
  return u.make ? `${u.make} ${u.model || ''}`.trim() : u.engineName || u.partName || 'Unknown';
}

function getUnitIcon(u) {
  return u._type === 'vehicle' ? <CarIcon /> : u._type === 'engine' ? <EngineIcon /> : <PartsIcon />;
}

// ── Multi-Step Container Creation Wizard ──────────────────────
function CreateContainerWizard({ user, role, onSave, onClose }) {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);

  // Step 1: Destination
  const [destination, setDestination] = useState('');
  const [destCountry, setDestCountry] = useState('');
  const [originPort, setOriginPort]   = useState('');

  // Step 2: Unit selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [unitSearch, setUnitSearch]   = useState('');
  const [unitCustomer, setUnitCustomer] = useState(''); // filter by customer
  const [unitTypeTab, setUnitTypeTab] = useState('all'); // all, vehicle, engine, part

  // Step 3: Container details
  const [number, setNumber]           = useState('');
  const [vessel, setVessel]           = useState('');
  const [voyage, setVoyage]           = useState('');
  const [containerSize, setContainerSize] = useState('');
  const [shippingLine, setShippingLine]   = useState('');

  const customers = DB.get('customers') || [];
  const available = getAvailableUnits(null);

  // Filter units — by customer, type tab, and search
  const filteredUnits = available.filter(u => {
    if (unitCustomer && u.assignedTo !== unitCustomer) return false;
    if (unitTypeTab === 'vehicle' && u._type !== 'vehicle') return false;
    if (unitTypeTab === 'engine' && u._type !== 'engine') return false;
    if (unitTypeTab === 'part' && u._type !== 'part') return false;
    if (unitSearch) {
      const q = unitSearch.toLowerCase();
      const name = getUnitName(u).toLowerCase();
      if (!name.includes(q) && !(u.sku || '').toLowerCase().includes(q) && !(u.chassis || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const selectedUnits = available.filter(u => selectedIds.has(u.id));

  const toggleUnit = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredUnits.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUnits.map(u => u.id)));
    }
  };

  const getCustomerName = (id) => {
    if (!id) return null;
    const c = customers.find(x => x.id === id);
    return c ? c.name : null;
  };

  const getCutLabel = (cutType) => CUT_TYPES.find(c => c.id === cutType)?.name || cutType;

  // Customer breakdown for review
  const customerBreakdown = {};
  selectedUnits.forEach(u => {
    const name = getCustomerName(u.assignedTo) || 'Unassigned';
    customerBreakdown[name] = (customerBreakdown[name] || 0) + 1;
  });

  const buildContainer = (status = 'planning') => ({
    id: DB.genId('CT'),
    number: number.trim(),
    vessel: vessel.trim(),
    voyage: voyage.trim(),
    origin: originPort,
    destination: destination.trim(),
    destinationCountry: destCountry.trim(),
    containerSize,
    shippingLine: shippingLine.trim(),
    eta: '',
    status,
    units: [...selectedIds],
    costs: defaultFixedCosts(),
    sealNumber: '',
    timeline: [],
    pricing: {},
    createdAt: new Date().toISOString().split('T')[0],
  });

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const containers = DB.get('containers') || [];
    const newContainer = buildContainer('planning');
    let result;
    if (draftId) {
      // Upgrade existing draft to planning
      result = await DB.setAsync('containers', containers.map(c => c.id === draftId ? { ...newContainer, id: draftId } : c));
    } else {
      result = await DB.setAsync('containers', [...containers, newContainer]);
    }
    logActivity.containerCreated?.(newContainer.number || 'New Container', user?.name || 'Staff');
    setSaving(false);
    onSave();
  };

  const [draftId, setDraftId] = useState(null); // track if we already saved a draft

  const handleSaveDraft = async () => {
    setSaving(true);
    const containers = DB.get('containers') || [];
    if (draftId) {
      // Update existing draft
      await DB.setAsync('containers', containers.map(c => c.id === draftId ? buildContainer('draft') : c));
    } else {
      // Create new draft
      const draft = buildContainer('draft');
      setDraftId(draft.id);
      await DB.setAsync('containers', [...containers, draft]);
    }
    setSaving(false);
    alert('✅ Draft saved!');
    // Do NOT close — stay on the wizard
  };

  const handleClose = () => {
    onClose();
  };

  const progress = (step / 4) * 100;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        {/* Progress bar */}
        <div style={{ height: 3, background: 'var(--bg3)' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, var(--blue), var(--cyan))', borderRadius: '0 3px 3px 0', transition: 'width 0.4s ease' }} />
        </div>

        <div className="modal-header">
          <div>
            <h3 className="modal-title">{t('cont_new_container')}</h3>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
              Step {step} of 4 — {['Destination', 'Select Units', 'Container Details', 'Review'][step - 1]}
            </div>
          </div>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>

        <div className="modal-body">

          {/* ── Step 1: Destination ── */}
          {step === 1 && (
            <div>
              <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 20 }}>
                Where is this container going? You can also set the origin port.
              </p>
              <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label className="form-label">Origin Port</label>
                  <select className="form-select" value={originPort} onChange={e => setOriginPort(e.target.value)}>
                    <option value="">Select port</option>
                    {Object.entries(WORLD_PORTS).sort(([a],[b]) => a.localeCompare(b)).map(([country, ports]) => (
                      <optgroup key={country} label={country}>
                        {ports.map(p => <option key={`${country}-${p}`} value={p}>{p}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Destination Port / City <span className="req">*</span></label>
                  <input className="form-input" value={destination} onChange={e => setDestination(e.target.value)} placeholder="e.g. Jebel Ali, Poti, Mombasa" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Destination Country</label>
                <input className="form-input" value={destCountry} onChange={e => setDestCountry(e.target.value)} placeholder="e.g. UAE, Georgia, Kenya" />
              </div>
            </div>
          )}

          {/* ── Step 2: Select Units ── */}
          {step === 2 && (
            <div>
              {/* Customer filter + search */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <select className="form-select" value={unitCustomer} onChange={e => setUnitCustomer(e.target.value)} style={{ width: 200, fontSize: 12 }}>
                  <option value="">All Customers</option>
                  {customers.filter(c => available.some(u => u.assignedTo === c.id)).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <div className="inv-search-wrap" style={{ flex: 1, minWidth: 160 }}>
                  <span className="inv-search-icon"><SearchIcon /></span>
                  <input className="inv-search" style={{ width: '100%' }} placeholder="Search by name, SKU, chassis…"
                    value={unitSearch} onChange={e => setUnitSearch(e.target.value)} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text2)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedIds.size > 0 && selectedIds.size === filteredUnits.length}
                      onChange={toggleAll} style={{ width: 14, height: 14, accentColor: 'var(--blue)' }} />
                    All
                  </label>
                  <span style={{ fontSize: 12, color: 'var(--blue)', fontWeight: 700 }}>
                    {selectedIds.size} selected
                  </span>
                </div>
              </div>

              {/* Type tabs: All / Vehicles / Engines / Parts */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
                {[
                  { id: 'all', label: 'All', count: available.filter(u => !unitCustomer || u.assignedTo === unitCustomer).length },
                  { id: 'vehicle', label: '🚗 Vehicles', count: available.filter(u => u._type === 'vehicle' && (!unitCustomer || u.assignedTo === unitCustomer)).length },
                  { id: 'engine', label: '⚙️ Engines', count: available.filter(u => u._type === 'engine' && (!unitCustomer || u.assignedTo === unitCustomer)).length },
                  { id: 'part', label: '🔧 Parts', count: available.filter(u => u._type === 'part' && (!unitCustomer || u.assignedTo === unitCustomer)).length },
                ].map(t => (
                  <button key={t.id} onClick={() => setUnitTypeTab(t.id)} style={{
                    padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: unitTypeTab === t.id ? 600 : 400,
                    background: unitTypeTab === t.id ? 'var(--blue)' : 'var(--bg3)',
                    color: unitTypeTab === t.id ? '#fff' : 'var(--text2)',
                    border: 'none', cursor: 'pointer',
                  }}>
                    {t.label} <span style={{ opacity: 0.7 }}>{t.count}</span>
                  </button>
                ))}
              </div>

              <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {filteredUnits.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--text3)', padding: 30, fontSize: 13 }}>
                    {unitSearch ? 'No units match your search' : 'No available units'}
                  </div>
                ) : filteredUnits.map(u => {
                  const checked = selectedIds.has(u.id);
                  const custName = getCustomerName(u.assignedTo);
                  return (
                    <div key={u.id} onClick={() => toggleUnit(u.id)} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 12px', background: checked ? 'rgba(88,166,255,0.08)' : 'var(--bg3)',
                      border: `1px solid ${checked ? 'var(--blue)' : 'var(--border)'}`,
                      borderRadius: 8, cursor: 'pointer', transition: 'all 0.12s',
                    }}>
                      <input type="checkbox" checked={checked} readOnly
                        style={{ width: 16, height: 16, accentColor: 'var(--blue)', flexShrink: 0, pointerEvents: 'none' }} />
                      <div style={{ color: 'var(--text3)', flexShrink: 0, width: 20 }}>{getUnitIcon(u)}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {getUnitName(u)}
                          {u.year ? ` (${u.year})` : ''}
                        </div>
                        <div style={{ fontSize: 10, display: 'flex', gap: 8, color: 'var(--text3)', flexWrap: 'wrap' }}>
                          <span style={{ color: 'var(--cyan)', fontFamily: 'monospace' }}>{u.sku}</span>
                          {u.chassis && <span>{u.chassis}</span>}
                          <span style={{ textTransform: 'capitalize' }}>{u._type}</span>
                          {u.cutType && <span style={{ color: 'var(--blue)' }}>{getCutLabel(u.cutType)}</span>}
                        </div>
                      </div>
                      {custName && (
                        <span style={{ fontSize: 10, color: 'var(--yellow)', background: 'rgba(210,153,34,0.12)', padding: '2px 8px', borderRadius: 10, flexShrink: 0, fontWeight: 600 }}>
                          → {custName}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Step 3: Container Details ── */}
          {step === 3 && (
            <div>
              <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 20 }}>
                Fill in what you know. All fields are optional — you can update them later.
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Container Number</label>
                  <input className="form-input" value={number} onChange={e => setNumber(e.target.value)} placeholder="e.g. MSCU1234567" />
                </div>
                <div className="form-group">
                  <label className="form-label">Container Size</label>
                  <select className="form-select" value={containerSize} onChange={e => setContainerSize(e.target.value)}>
                    <option value="">Select</option>
                    {CONTAINER_SIZES.map(s => <option key={s} value={s}>{SIZE_LABELS[s]}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Shipping Line</label>
                  <input className="form-input" value={shippingLine} onChange={e => setShippingLine(e.target.value)} placeholder="e.g. MSC, Evergreen" />
                </div>
              </div>
              <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label className="form-label">Vessel Name</label>
                  <input className="form-input" value={vessel} onChange={e => setVessel(e.target.value)} placeholder="e.g. NYK VENUS" />
                </div>
                <div className="form-group">
                  <label className="form-label">Voyage Number</label>
                  <input className="form-input" value={voyage} onChange={e => setVoyage(e.target.value)} placeholder="e.g. V025E" />
                </div>
              </div>
            </div>
          )}

          {/* ── Step 4: Review ── */}
          {step === 4 && (
            <div>
              {/* Route */}
              <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Route</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {originPort || '—'} → {destination || '—'}
                  {destCountry ? `, ${destCountry}` : ''}
                </div>
                {(number || containerSize || vessel) && (
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {number && <span>📦 {number}</span>}
                    {containerSize && <span>{SIZE_LABELS[containerSize]}</span>}
                    {vessel && <span>🚢 {vessel}{voyage ? ` · ${voyage}` : ''}</span>}
                    {shippingLine && <span>{shippingLine}</span>}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--blue)' }}>{selectedUnits.length}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Total Units</div>
                </div>
                <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--green)' }}>{selectedUnits.filter(u => u._type === 'vehicle').length}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Vehicles</div>
                </div>
                <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--purple)' }}>{selectedUnits.filter(u => u._type !== 'vehicle').length}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Engines / Parts</div>
                </div>
              </div>

              {/* Customer breakdown */}
              {Object.keys(customerBreakdown).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Customer Breakdown</div>
                  {Object.entries(customerBreakdown).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                    <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <span>{name}</span>
                      <span style={{ fontWeight: 700, color: 'var(--blue)' }}>{count} unit{count !== 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Unit list */}
              <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Units</div>
              <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                {selectedUnits.map(u => (
                  <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', background: 'var(--bg3)', borderRadius: 6, fontSize: 12 }}>
                    <span style={{ color: 'var(--text3)', width: 16 }}>{getUnitIcon(u)}</span>
                    <span style={{ fontWeight: 600, flex: 1 }}>{getUnitName(u)}</span>
                    <span style={{ color: 'var(--cyan)', fontFamily: 'monospace', fontSize: 10 }}>{u.sku}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer: navigation */}
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <button type="button" className="btn btn-secondary" onClick={step > 1 ? () => setStep(step - 1) : handleClose} disabled={saving}>
            {step > 1 ? '← Back' : 'Cancel'}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Saving...' : '💾 Save Draft'}
            </button>
            {step < 4 ? (
              <button className="btn btn-primary"
                disabled={(step === 2 && selectedIds.size === 0) || saving}
                onClick={() => setStep(step + 1)}>
                Next →
              </button>
            ) : (
              <button className="btn btn-success" onClick={handleSave} disabled={saving}>
                <CheckIcon />{saving ? 'Creating...' : 'Create Container'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Edit Container Modal (simpler, single page) ──────────────
function EditContainerModal({ initialData, user, role, onSave, onClose }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({ ...initialData });
  const [unitSearch, setUnitSearch] = useState('');
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const available = getAvailableUnits(initialData.id);
  const allForPicker = [...available, ...(initialData.units || []).map(id => {
    const units = [
      ...(DB.get('vehicles') || []).map(u => ({ ...u, _type: 'vehicle' })),
      ...(DB.get('engines') || []).map(u => ({ ...u, _type: 'engine' })),
      ...(DB.get('spareParts') || []).map(u => ({ ...u, _type: 'part' })),
    ];
    return units.find(u => u.id === id);
  }).filter(Boolean)];

  // Deduplicate
  const seen = new Set();
  const uniqueUnits = allForPicker.filter(u => { if (seen.has(u.id)) return false; seen.add(u.id); return true; });

  const filtered = uniqueUnits.filter(u => {
    if (!unitSearch) return true;
    const q = unitSearch.toLowerCase();
    return getUnitName(u).toLowerCase().includes(q) || (u.sku || '').toLowerCase().includes(q);
  });

  const selectedIds = new Set(form.units || []);
  const toggleUnit = (id) => set('units', selectedIds.has(id) ? [...selectedIds].filter(x => x !== id) : [...selectedIds, id]);

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const containers = DB.get('containers') || [];
    const updatedContainer = { ...initialData, ...form };
    const result = await DB.setAsync('containers', containers.map(c => c.id === initialData.id ? updatedContainer : c));
    if (form.status !== initialData.status) {
      const allU = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
      const cIds = [...new Set((initialData.units || []).map(uid => allU.find(u => u.id === uid)?.assignedTo).filter(Boolean))];
      logActivity.containerStatusChanged?.(form.number || initialData.number || initialData.id, form.status, cIds);
    }
    setSaving(false);
    onSave();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Edit Container</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Container Number</label>
                <input className="form-input" value={form.number} onChange={e => set('number', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Vessel</label>
                <input className="form-input" value={form.vessel} onChange={e => set('vessel', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Voyage</label>
                <input className="form-input" value={form.voyage} onChange={e => set('voyage', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Size</label>
                <select className="form-select" value={form.containerSize || ''} onChange={e => set('containerSize', e.target.value)}>
                  <option value="">Select</option>
                  {CONTAINER_SIZES.map(s => <option key={s} value={s}>{SIZE_LABELS[s]}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                  {EXPANDED_STATUSES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Seal Number</label>
                <input className="form-input" value={form.sealNumber || ''} onChange={e => set('sealNumber', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Origin Port</label>
                <select className="form-select" value={form.origin} onChange={e => set('origin', e.target.value)}>
                  <option value="">Select</option>
                  {Object.entries(WORLD_PORTS).sort(([a],[b]) => a.localeCompare(b)).map(([country, ports]) => (
                    <optgroup key={country} label={country}>
                      {ports.map(p => <option key={`${country}-${p}`} value={p}>{p}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Destination</label>
                <input className="form-input" value={form.destination} onChange={e => set('destination', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">ETA</label>
                <input type="date" className="form-input" value={form.eta || ''} onChange={e => set('eta', e.target.value)} />
              </div>
            </div>
            <div className="form-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Planned Dep.</label>
                <input type="date" className="form-input" value={form.plannedDeparture || ''} onChange={e => set('plannedDeparture', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Actual Dep.</label>
                <input type="date" className="form-input" value={form.actualDeparture || ''} onChange={e => set('actualDeparture', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Est. Arrival</label>
                <input type="date" className="form-input" value={form.eta || ''} onChange={e => set('eta', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" style={{ fontSize: 10 }}>Actual Arrival</label>
                <input type="date" className="form-input" value={form.actualArrival || ''} onChange={e => set('actualArrival', e.target.value)} />
              </div>
            </div>
            {role.canPrice && (
              <div style={{ fontSize: 11, color: 'var(--text3)', padding: '8px 12px', background: 'var(--bg3)', borderRadius: 8 }}>
                💰 Edit costs in the Costs tab of the container detail view.
              </div>
            )}

            {/* Units */}
            <div style={{ marginTop: 8, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
              <label className="form-label">Units ({(form.units || []).length})</label>
              <input className="form-input" placeholder="Search…" value={unitSearch} onChange={e => setUnitSearch(e.target.value)} style={{ marginBottom: 8 }} />
              <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                {filtered.map(u => {
                  const checked = selectedIds.has(u.id);
                  return (
                    <div key={u.id} onClick={() => toggleUnit(u.id)} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                      background: checked ? 'rgba(88,166,255,0.08)' : 'var(--bg3)',
                      border: `1px solid ${checked ? 'var(--blue)' : 'var(--border)'}`,
                      borderRadius: 6, cursor: 'pointer', fontSize: 12,
                    }}>
                      <input type="checkbox" checked={checked} readOnly style={{ accentColor: 'var(--blue)', pointerEvents: 'none' }} />
                      <span style={{ color: 'var(--text3)' }}>{getUnitIcon(u)}</span>
                      <span style={{ fontWeight: 600, flex: 1 }}>{getUnitName(u)}</span>
                      <span style={{ color: 'var(--cyan)', fontFamily: 'monospace', fontSize: 10 }}>{u.sku}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary"><CheckIcon />Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Enhanced Timeline Section ─────────────────────────────────
const TIMELINE_EVENTS = [
  { id: 'departed',      label: 'Departed',       icon: '🚢' },
  { id: 'transshipment', label: 'Transshipment',  icon: '🔄' },
  { id: 'approaching',   label: 'Approaching',    icon: '📍' },
  { id: 'arrived',       label: 'Arrived',        icon: '✅' },
  { id: 'delay',         label: 'Delay',          icon: '⚠️' },
  { id: 'customs',       label: 'Customs',        icon: '📋' },
  { id: 'delivered',     label: 'Delivered',       icon: '📦' },
  { id: 'note',          label: 'Note',           icon: '📝' },
];

function TimelineSection({ container, user, isCustomer, onUpdate }) {
  const { t } = useLanguage();
  const [note, setNote] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [eventType, setEventType] = useState('note');
  const [location, setLocation] = useState('');
  const [photo, setPhoto] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const photoRef = React.useRef();
  const timeline = container.timeline || [];

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    e.target.value = '';
    setUploading(true);
    try {
      const compressed = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const img = new Image();
          img.onload = () => {
            const MAX = 1000; let { width, height } = img;
            if (width > MAX || height > MAX) { if (width > height) { height = Math.round(height * MAX / width); width = MAX; } else { width = Math.round(width * MAX / height); height = MAX; } }
            const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
          };
          img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
      });
      const { uploadImage } = await import('../data/database');
      const url = await uploadImage(compressed);
      setPhoto(url);
    } catch {}
    setUploading(false);
  };

  const addEntry = () => {
    if (!note.trim()) return;
    const evt = TIMELINE_EVENTS.find(e => e.id === eventType) || TIMELINE_EVENTS[7];
    const entry = {
      id: `tl-${Date.now()}`, date, event_type: eventType, icon: evt.icon,
      location: location.trim(), event: note.trim(),
      photo: photo || '', added_by: user?.id, added_by_name: user?.name,
      addedAt: new Date().toISOString(),
    };
    const updated = { ...container, timeline: [...timeline, entry] };
    const containers = DB.get('containers') || [];
    DB.setAsync('containers', containers.map(c => c.id === container.id ? updated : c));
    onUpdate(updated);
    // Notify customers about timeline event
    const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
    const custIds = [...new Set((container.units || []).map(uid => allUnits.find(u => u.id === uid)?.assignedTo).filter(Boolean))];
    logActivity.containerTimelineEvent?.(container.number || container.id, note.trim(), custIds);
    setNote(''); setLocation(''); setPhoto(''); setShowForm(false);
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Timeline</div>
        {!isCustomer && !showForm && (
          <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => setShowForm(true)}>+ Add Update</button>
        )}
      </div>

      {/* Add entry form (staff only) */}
      {!isCustomer && showForm && (
        <div style={{ padding: 12, background: 'var(--bg3)', borderRadius: 10, marginBottom: 12 }}>
          <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: 8 }}>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: 10 }}>Date</label>
              <input type="date" className="form-input" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: 10 }}>Event Type</label>
              <select className="form-select" value={eventType} onChange={e => setEventType(e.target.value)}>
                {TIMELINE_EVENTS.map(ev => <option key={ev.id} value={ev.id}>{ev.icon} {ev.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 8 }}>
            <label className="form-label" style={{ fontSize: 10 }}>Location</label>
            <input className="form-input" value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Yokohama Port, Singapore" />
          </div>
          <div className="form-group" style={{ marginBottom: 8 }}>
            <label className="form-label" style={{ fontSize: 10 }}>Note <span className="req">*</span></label>
            <textarea className="form-input" rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="What happened…" style={{ resize: 'vertical' }} />
          </div>
          {/* Photo */}
          <div style={{ marginBottom: 10 }}>
            {photo ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={photo} alt="" style={{ width: 80, height: 60, borderRadius: 6, objectFit: 'cover' }} />
                <button onClick={() => setPhoto('')} style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: 'var(--red)', border: 'none', color: '#fff', fontSize: 9, cursor: 'pointer' }}>×</button>
              </div>
            ) : (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => photoRef.current?.click()}>
                📷 Add Photo (optional)
              </button>
            )}
            <input ref={photoRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handlePhoto} />
            {uploading && <span style={{ fontSize: 11, color: 'var(--blue)', marginLeft: 8 }}>Uploading...</span>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowForm(false); setPhoto(''); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={!note.trim() || uploading} onClick={addEntry}>Submit Update</button>
          </div>
        </div>
      )}

      {/* Timeline entries */}
      {timeline.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative', paddingLeft: 24 }}>
          <div style={{ position: 'absolute', left: 7, top: 6, bottom: 6, width: 2, background: 'var(--border)', borderRadius: 1 }} />
          {[...timeline].sort((a, b) => (b.date || '').localeCompare(a.date || '')).map((entry, i) => {
            const evt = TIMELINE_EVENTS.find(e => e.id === entry.event_type);
            return (
              <div key={entry.id || i} style={{ position: 'relative', paddingBottom: 16 }}>
                <div style={{ position: 'absolute', left: -22, top: 2, fontSize: 14 }}>{entry.icon || evt?.icon || '📝'}</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 2 }}>
                  <span style={{ fontSize: 10, color: 'var(--text3)' }}>{entry.date}</span>
                  {evt && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', background: 'var(--bg3)', borderRadius: 4, color: 'var(--text2)', textTransform: 'uppercase' }}>{evt.label}</span>}
                  {entry.location && <span style={{ fontSize: 10, color: 'var(--blue)', fontWeight: 600 }}>📍 {entry.location}</span>}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text)' }}>{entry.event}</div>
                {entry.added_by_name && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>by {entry.added_by_name}</div>}
                {entry.photo && (
                  <img src={entry.photo} alt="" onClick={() => window.open(entry.photo, '_blank')}
                    style={{ width: 80, height: 60, borderRadius: 6, objectFit: 'cover', marginTop: 6, cursor: 'pointer' }} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Containers Page ──────────────────────────────────────
function Containers({ user, currency, lang, custFilter, setCustFilter }) {
  const { t } = useLanguage();
  const [openId, setOpenId]         = useState(null);
  const [containers, setContainers] = useState(DB.get('containers') || []);
  const [search, setSearch]         = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editData, setEditData]     = useState(null);
  const [detailTab, setDetailTab]   = useState('overview');

  // Refresh containers from Supabase on mount only (not during active use)
  useEffect(() => {
    DB.refresh('containers').then(() => {
      setContainers(DB.get('containers') || []);
      // Wave 3.0c-fix: open container to Costs tab if navigated from Pricing page
      const openCostId = sessionStorage.getItem('bs_open_container_costs');
      if (openCostId) {
        sessionStorage.removeItem('bs_open_container_costs');
        const allContainers = DB.get('containers') || [];
        if (allContainers.find(c => c.id === openCostId)) {
          openContainerTab(openCostId);
          setOpenId(openCostId);
          setDetailTab('costs');
        }
      }
    }).catch(() => {});
  }, []);
  // Excel-style tabs: track which containers are open as tabs
  const [openTabs, setOpenTabs]     = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('bs_container_tabs') || '[]'); } catch { return []; }
  });
  const [activeTab, setActiveTab]   = useState(null);

  const openContainerTab = (id) => {
    setOpenTabs(prev => {
      const next = prev.includes(id) ? prev : [...prev, id];
      sessionStorage.setItem('bs_container_tabs', JSON.stringify(next));
      return next;
    });
    setActiveTab(id);
  };
  const closeTab = (id, e) => {
    e?.stopPropagation();
    setOpenTabs(prev => {
      const next = prev.filter(t => t !== id);
      sessionStorage.setItem('bs_container_tabs', JSON.stringify(next));
      return next;
    });
    if (activeTab === id) setActiveTab(null);
  };

  const role = ROLES[user.role] || {};
  const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
  const getUnit = (id) => allUnits.find(u => u.id === id);

  const isCustomer = role.isCustomer;
  const myUnitIds = isCustomer ? new Set(allUnits.filter(u => u.assignedTo === user.id).map(u => u.id)) : null;

  // Customer filter for admin/accountant
  const filterByCustomer = (containerList) => {
    if (isCustomer) {
      return containerList.filter(c => {
        if (c.units?.some(uid => myUnitIds.has(uid))) return true;
        if (c.unitsData?.some(ud => ud.customer_id === user.id)) return true;
        return false;
      });
    }
    if (custFilter) {
      return containerList.filter(c => {
        const unitIds = c.units || [];
        return unitIds.some(uid => { const u = allUnits.find(x => x.id === uid); return u?.assignedTo === custFilter; });
      });
    }
    return containerList;
  };
  const customerContainers = filterByCustomer(containers);

  const filteredContainers = customerContainers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.number?.toLowerCase().includes(q) || c.vessel?.toLowerCase().includes(q) || c.origin?.toLowerCase().includes(q) || c.destination?.toLowerCase().includes(q) || c.sealNumber?.toLowerCase().includes(q);
  });

  const toggleContainer = (id) => {
    if (openId === id) {
      setOpenId(null);
      setDetailTab('overview');
    } else {
      setOpenId(id);
      setDetailTab('overview');
    }
  };

  const handleMarkArrived = (containerId) => {
    const updated = containers.map(c => c.id === containerId ? { ...c, status: 'delivered' } : c);
    const container = containers.find(c => c.id === containerId);
    DB.setAsync('containers', updated);
    setContainers(updated);
    logActivity.containerArrived(container.number, user.name);
  };

  const handleSaved = () => {
    setShowCreate(false);
    setEditData(null);
    // Read from localStorage (already has the latest from DB.setAsync)
    // Don't refresh from Supabase here — it may not have propagated yet
    setContainers(DB.get('containers') || []);
  };

  const handleDelete = (containerId, e) => {
    e.stopPropagation();
    const cont = containers.find(c => c.id === containerId);
    if (!cont) return;
    if (!['planning', 'booked'].includes(cont.status)) {
      alert('Cannot delete — loading in progress. Only containers in Planning or Booked status can be deleted.');
      return;
    }
    if (!window.confirm(`Delete container ${cont.number || cont.id}? All units inside will be released back to available.`)) return;
    // Release units back to available via central function
    (Array.isArray(cont.units) ? cont.units : []).forEach(uid => {
      ['vehicle', 'engine', 'part'].forEach(uType => {
        const key = uType === 'vehicle' ? 'vehicles' : uType === 'engine' ? 'engines' : 'spareParts';
        const list = DB.get(key) || [];
        if (list.find(u => u.id === uid)) {
          centralUpdateStatus(uid, uType, 'available', { updatedBy: 'System', extraFields: { containerId: '' } });
        }
      });
    });
    const updated = containers.filter(c => c.id !== containerId);
    DB.setAsync('containers', updated);
    setContainers(updated);
  };

  const handleTimelineUpdate = (u) => setContainers(prev => prev.map(c => c.id === u.id ? u : c));

  const statusClass = (s) => {
    if (['in_transit', 'shipped'].includes(s)) return 'transit';
    if (['delivered', 'closed'].includes(s)) return 'available';
    if (['at_port', 'customs'].includes(s)) return 'assigned';
    return 'pending';
  };

  // Find container details for tabs
  const getTabContainer = (id) => containers.find(c => c.id === id);
  const customers = DB.get('customers') || [];
  const getCustomerName = (cid) => customers.find(c => c.id === cid)?.name || '';
  const getContainerCustomer = (cont) => {
    if (!cont) return '';
    const unitIds = cont.units || [];
    const assignees = unitIds.map(uid => allUnits.find(u => u.id === uid)?.assignedTo).filter(Boolean);
    if (assignees.length === 0) return '';
    const custId = assignees[0];
    return getCustomerName(custId);
  };

  // If a tab is active, show that container's detail instead of the list
  const activeContainer = activeTab ? getTabContainer(activeTab) : null;

  return (
    <div>
      {/* ── Excel-style container tabs ── */}
      {!isCustomer && (
        <div style={{ display: 'flex', gap: 2, marginBottom: 16, overflowX: 'auto', paddingBottom: 4, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setActiveTab(null)} style={{
            padding: '8px 16px', borderRadius: '8px 8px 0 0', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border)', borderBottom: !activeTab ? '2px solid var(--blue)' : '1px solid transparent',
            background: !activeTab ? 'var(--bg1)' : 'var(--bg3)', color: !activeTab ? 'var(--text1)' : 'var(--text3)',
          }}>
            📋 All ({filteredContainers.length})
          </button>
          {openTabs.map(tabId => {
            const tc = getTabContainer(tabId);
            if (!tc) return null;
            const custName = getContainerCustomer(tc);
            return (
              <div key={tabId} onClick={() => setActiveTab(tabId)} style={{
                padding: '8px 14px', borderRadius: '8px 8px 0 0', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border)', borderBottom: activeTab === tabId ? '2px solid var(--blue)' : '1px solid var(--border)',
                background: activeTab === tabId ? 'var(--bg1)' : 'var(--bg2)', color: activeTab === tabId ? 'var(--text1)' : 'var(--text3)', whiteSpace: 'nowrap',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 11 }}>{tc.number || 'Draft'}</div>
                  {custName && <div style={{ fontSize: 9, color: 'var(--text3)' }}>{tc.destinationCountry ? `${tc.destinationCountry} · ` : ''}{custName}</div>}
                </div>
                <button onClick={e => closeTab(tabId, e)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>✕</button>
              </div>
            );
          })}
          {role.canContainer && (
            <button onClick={() => setShowCreate(true)} style={{ padding: '8px 14px', borderRadius: '8px 8px 0 0', fontSize: 16, cursor: 'pointer', border: '1px dashed var(--border)', background: 'transparent', color: 'var(--text3)' }} title="New container">+</button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 18 }}>{activeContainer ? (activeContainer.number || 'Container') : t('containers')}</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {!activeTab && !isCustomer && setCustFilter && <CustomerFilter value={custFilter} onChange={setCustFilter} />}
          {!activeTab && (
            <div style={{ position: 'relative' }}>
              <span className="cont-search-icon"><SearchIcon /></span>
              <input className="units-search" style={{ paddingLeft: 30, width: 220 }} placeholder="Search container, vessel, seal…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          )}
          {role.canContainer && (
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              <PlusIcon />{t('cont_new_container')}
            </button>
          )}
        </div>
      </div>

      {!activeTab && !isCustomer && setCustFilter && <CustomerFilterBanner value={custFilter} onChange={setCustFilter} />}

      {!activeTab && filteredContainers.length === 0 ? (
        <div className="empty">
          <ContainerIcon />
          <div className="empty-title">{isCustomer ? 'No Containers Yet' : t('noData')}</div>
          {isCustomer && <div className="empty-text">Your units will appear here once they are loaded for shipping.</div>}
        </div>
      ) : (activeTab ? [containers.find(c => c.id === activeTab)].filter(Boolean) : filteredContainers).map(container => {
        const units = container.units?.map(getUnit).filter(Boolean) || [];
        const displayUnits = isCustomer ? units.filter(u => u.assignedTo === user.id) : units;
        const isOpen = activeTab === container.id || openId === container.id;

        return (
          <div key={container.id} className="cont-card">
            <div className="cont-header" onClick={() => { if (!activeTab) openContainerTab(container.id); toggleContainer(container.id); }}>
              <div className="cont-icon"><ContainerIcon /></div>
              <div className="cont-info">
                <div className="cont-number">
                  {container.number || 'Pending'}
                  {container.containerSize && <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 8, fontWeight: 400 }}>{SIZE_LABELS[container.containerSize] || container.containerSize}</span>}
                  <span className={`status status-${statusClass(container.status)}`}>{(container.status || '').replace(/_/g, ' ')}</span>
                </div>
                <div className="cont-meta">
                  {container.vessel}{container.voyage ? ` · ${container.voyage}` : ''} • {container.origin || '—'} → {container.destination || '—'}
                  {container.destinationCountry ? `, ${container.destinationCountry}` : ''}
                </div>
              </div>
              <div className="cont-stats">
                <div className="cont-stat">
                  <div className="cont-stat-val">{displayUnits.length}</div>
                  <div className="cont-stat-lbl">Units</div>
                </div>
                <div className="cont-stat">
                  <div className="cont-stat-val">{container.eta || '—'}</div>
                  <div className="cont-stat-lbl">ETA</div>
                </div>
                {(role.canContainer || role.canPrice) && (
                  <div className="cont-stat">
                    <div className="cont-stat-val">{formatPrice(calculateContainerTotalJPY(container.costs), currency)}</div>
                    <div className="cont-stat-lbl">Total Cost</div>
                  </div>
                )}
              </div>
              <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0, marginRight: 4, fontSize: 10 }}
                onClick={e => { e.stopPropagation(); generatePackingListPDF(container, { customerId: role.isCustomer ? user.id : null }); }}>📄 Packing List</button>
              {role.canContainer && (
                <button className="btn btn-secondary btn-sm" style={{ flexShrink: 0, marginRight: 6 }}
                  onClick={e => { e.stopPropagation(); setEditData(container); }}><EditIcon /></button>
              )}
              <span className="cont-chevron" style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><ChevronDownIcon /></span>
            </div>

            <div className={`cont-units ${isOpen ? 'open' : ''}`}>
              {isOpen && (
                <>
                  {/* Inner tab bar */}
                  <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 12, overflowX: 'auto' }}>
                    {[
                      { id: 'overview', label: '📋 Overview' },
                      { id: 'units', label: '📦 Units (' + (container.units || []).length + ')' },
                      { id: 'loading', label: '🏗️ Loading', show: role.canContainer },
                      { id: 'costs', label: '💰 Costs', show: role.canPrice || role.canContainer },
                      { id: 'bl', label: '📄 B/L' },
                      { id: 'timeline', label: '🕐 Timeline' },
                    ].filter(t => t.show !== false).map(t => (
                      <button key={t.id} onClick={() => setDetailTab(t.id)} style={{
                        padding: '8px 14px', fontSize: 11, fontWeight: detailTab === t.id ? 600 : 400,
                        background: detailTab === t.id ? 'var(--bg2)' : 'transparent',
                        color: detailTab === t.id ? 'var(--text1)' : 'var(--text3)',
                        border: 'none', borderBottom: detailTab === t.id ? '2px solid var(--blue)' : '2px solid transparent',
                        cursor: 'pointer', whiteSpace: 'nowrap',
                      }}>{t.label}</button>
                    ))}
                  </div>

                  {/* Overview tab */}
                  {detailTab === 'overview' && (
                    <>
                      <div style={{ marginBottom: 12, overflowX: 'auto' }}>
                        <ContainerTimeline status={container.status} timeline={container.timeline} />
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12, padding: '0 2px' }}>
                        {container.sealNumber && <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '6px 12px', fontSize: 12 }}><span style={{ color: 'var(--text3)' }}>Seal: </span><strong style={{ color: 'var(--cyan)', fontFamily: 'monospace' }}>{container.sealNumber}</strong></div>}
                        {container.plannedDeparture && <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '6px 12px', fontSize: 12 }}><span style={{ color: 'var(--text3)' }}>Plan Dep: </span>{container.plannedDeparture}</div>}
                        {container.actualDeparture && <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '6px 12px', fontSize: 12 }}><span style={{ color: 'var(--text3)' }}>Departed: </span><strong style={{ color: 'var(--green)' }}>{container.actualDeparture}</strong></div>}
                        {container.actualArrival && <div style={{ background: 'var(--bg3)', borderRadius: 6, padding: '6px 12px', fontSize: 12 }}><span style={{ color: 'var(--text3)' }}>Arrived: </span><strong style={{ color: 'var(--green)' }}>{container.actualArrival}</strong></div>}
                      </div>
                      {role.canArrival && container.status === 'in_transit' && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                          <button className="btn btn-success" onClick={e => { e.stopPropagation(); handleMarkArrived(container.id); }}><CheckIcon />Mark as Delivered</button>
                        </div>
                      )}
                      {role.canDelete && (
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                          {!['planning', 'booked'].includes(container.status) && (
                            <span style={{ fontSize: 10, color: 'var(--text3)' }}>Cannot delete — loading in progress</span>
                          )}
                          <button className="btn btn-secondary btn-sm"
                            style={{ color: 'var(--red)', borderColor: 'var(--red)', opacity: ['planning', 'booked'].includes(container.status) ? 1 : 0.4 }}
                            disabled={!['planning', 'booked'].includes(container.status)}
                            onClick={e => handleDelete(container.id, e)}>
                            <DeleteIcon /> Delete
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {/* Units tab */}
                  {detailTab === 'units' && (
                    <>
                      {displayUnits.length === 0 && <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No units loaded</div>}
                      {displayUnits.map(unit => {
                        const unitData = (container.unitsData || []).find(ud => ud.unit_id === unit.id);
                        const loadingPhotos = unitData?.loading_photos || [];
                        return (
                          <div key={unit.id} className="cont-unit" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div className="cont-unit-img">
                                {unit.images?.length ? <img src={unit.images[0]} alt="" /> : unit.make ? <CarIcon /> : unit.engineName ? <EngineIcon /> : <PartsIcon />}
                              </div>
                              <div className="cont-unit-info">
                                <div className="cont-unit-name">{unit.make ? `${unit.make} ${unit.model}` : unit.engineName || unit.partName}</div>
                                <div className="cont-unit-sku">{unit.sku}</div>
                              </div>
                              {unitData?.loaded_at && (
                                <span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 700, flexShrink: 0 }}>✅ Loaded</span>
                              )}
                              {!unitData?.loaded_at && (
                                <span className={`status status-${unit.status === 'in_transit' ? 'transit' : unit.status === 'assigned' ? 'assigned' : 'available'}`} style={{ fontSize: 10 }}>{(unit.status || '').replace('_', ' ')}</span>
                              )}
                            </div>
                            {/* Loading photos */}
                            {loadingPhotos.length > 0 && (
                              <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                                {loadingPhotos.map((url, i) => (
                                  <img key={i} src={url} alt="" onClick={() => window.open(url, '_blank')}
                                    style={{ width: 48, height: 36, borderRadius: 4, objectFit: 'cover', cursor: 'pointer' }} />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Loading tab */}
                  {detailTab === 'loading' && role.canContainer && (
                    <>
                      <ContainerLoading container={container} user={user} onUpdate={handleTimelineUpdate} />
                      <ContainerSeal container={container} user={user} onUpdate={handleTimelineUpdate} />
                    </>
                  )}

                  {/* Costs tab */}
                  {detailTab === 'costs' && (role.canPrice || role.canContainer) && (
                    <ContainerCostsEditor container={container} onSave={() => setContainers(DB.get('containers') || [])} />
                  )}

                  {/* B/L tab */}
                  {detailTab === 'bl' && (
                    <ContainerBL container={container} user={user} isCustomer={isCustomer} onUpdate={handleTimelineUpdate} />
                  )}

                  {/* Timeline tab */}
                  {detailTab === 'timeline' && (
                    <>
                      <TimelineSection container={container} user={user} isCustomer={isCustomer} onUpdate={handleTimelineUpdate} />
                      {isCustomer && <ContainerArrival container={container} user={user} onUpdate={handleTimelineUpdate} />}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}

      {showCreate && <CreateContainerWizard user={user} role={role} onSave={handleSaved} onClose={() => setShowCreate(false)} />}
      {editData && <EditContainerModal initialData={editData} user={user} role={role} onSave={handleSaved} onClose={() => setEditData(null)} />}
    </div>
  );
}

export default Containers;
